import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, AlertCircle, Plus, Tag, Euro, Calendar, CheckCircle, XCircle, Clock, ChevronDown, Pause, PlayCircle } from 'lucide-react';
import Button from '../ui/Button';
import Dropdown, { DropdownOption } from '../ui/Dropdown';
import { AdminSubscriptionRecord } from '../../types';
import { adminApi } from '../../services/api';

interface EditSubscriptionModalProps {
  isOpen: boolean;
  subscription: AdminSubscriptionRecord | null;
  onClose: () => void;
  onSuccess: (subscription: AdminSubscriptionRecord) => void;
}

// Predefined subscription types with colors
const SUBSCRIPTION_TYPES = [
  { id: 'e-commerce', label: 'E-Commerce', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { id: 'internal', label: 'Internal', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  { id: 'grocery', label: 'Grocery', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { id: 'tech', label: 'Tech', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
];

interface FormData {
  types: string[];
  status: 'active' | 'inactive' | 'suspended' | 'pending' | 'trialing';
  planId: string;
  amount: number;
  nextBillingDate: string;
}

interface BillingChangeModalProps {
  isOpen: boolean;
  onConfirm: (applyNextPeriod: boolean) => void;
  onCancel: () => void;
}

const BillingChangeModal: React.FC<BillingChangeModalProps> = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-surface w-full max-w-md rounded-xl shadow-2xl border dark:border-border-color border-light-border-color animate-in fade-in zoom-in duration-200">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
              <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-bold dark:text-white text-gray-900">
              Billing Period Change
            </h3>
          </div>
          <p className="text-sm dark:text-gray-300 text-gray-600 mb-6">
            You've changed the Amount or Plan but haven't updated the Next Billing Date. 
            Should these changes take effect in the <strong>next billing period</strong>?
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={onCancel}
              className="flex-1"
            >
              No, I'll update billing date
            </Button>
            <Button
              variant="primary"
              onClick={() => onConfirm(true)}
              className="flex-1"
            >
              Yes, apply next period
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({ 
  isOpen,
  subscription, 
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);
  const [customTypes, setCustomTypes] = useState<{ id: string; label: string; color: string }[]>([]);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [pendingChangesNextPeriod, setPendingChangesNextPeriod] = useState<{ amount: boolean; plan: boolean }>({ amount: false, plan: false });
  
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  
  // Close type dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeSelector(false);
        setShowNewTypeInput(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Original values for comparison
  const [originalValues, setOriginalValues] = useState({
    amount: 0,
    planId: '',
    nextBillingDate: '',
  });

  const [formData, setFormData] = useState<FormData>({
    types: [],
    status: 'active',
    planId: '',
    amount: 0,
    nextBillingDate: '',
  });

  // Update form data when subscription changes
  useEffect(() => {
    if (subscription) {
      setOriginalValues({
        amount: subscription.billing?.amount || 0,
        planId: subscription.planId || '',
        nextBillingDate: subscription.billing?.nextBillingDate || '',
      });
      setFormData({
        types: subscription.types || [],
        status: subscription.status || 'active',
        planId: subscription.planId || '',
        amount: subscription.billing?.amount || 0,
        nextBillingDate: subscription.billing?.nextBillingDate 
          ? new Date(subscription.billing.nextBillingDate).toISOString().split('T')[0]
          : '',
      });
      // Reset state when opening modal
      setError(null);
      setShowTypeSelector(false);
      setShowNewTypeInput(false);
      setNewTypeName('');
      setPendingChangesNextPeriod({ amount: false, plan: false });
    }
  }, [subscription]);

  const allTypes = [...SUBSCRIPTION_TYPES, ...customTypes];

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setError(null);
    // Clear pending changes note if user modifies the field again
    if (key === 'amount') {
      setPendingChangesNextPeriod(prev => ({ ...prev, amount: false }));
    }
    if (key === 'planId') {
      setPendingChangesNextPeriod(prev => ({ ...prev, plan: false }));
    }
  };

  const addType = (typeId: string) => {
    if (!formData.types.includes(typeId)) {
      updateField('types', [...formData.types, typeId]);
    }
    setShowTypeSelector(false);
  };

  const removeType = (typeId: string) => {
    updateField('types', formData.types.filter(t => t !== typeId));
  };

  const handleAddNewType = () => {
    if (newTypeName.trim()) {
      const newTypeId = newTypeName.toLowerCase().replace(/\s+/g, '-');
      const colors = [
        'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
        'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
        'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
        'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      ];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      
      const newType = {
        id: newTypeId,
        label: newTypeName.trim(),
        color: randomColor,
      };
      
      setCustomTypes(prev => [...prev, newType]);
      addType(newTypeId);
      setNewTypeName('');
      setShowNewTypeInput(false);
    }
  };

  const getTypeConfig = (typeId: string) => {
    return allTypes.find(t => t.id === typeId) || { 
      id: typeId, 
      label: typeId, 
      color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' 
    };
  };

  const getStatusConfig = (status: string) => {
    const config: Record<string, { bg: string; icon: React.ReactNode; label: string }> = {
      active: { 
        bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Active'
      },
      inactive: { 
        bg: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Inactive'
      },
      suspended: { 
        bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        icon: <Pause className="w-4 h-4" />,
        label: 'Suspended'
      },
      pending: { 
        bg: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        icon: <Clock className="w-4 h-4" />,
        label: 'Pending'
      },
      trialing: { 
        bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        icon: <PlayCircle className="w-4 h-4" />,
        label: 'Trialing'
      },
    };
    return config[status] || config.inactive;
  };

  const statusOptions: FormData['status'][] = ['active', 'inactive', 'suspended', 'pending', 'trialing'];

  const planOptions: DropdownOption[] = [
    { value: 'basic-monthly', label: 'Basic Monthly' },
    { value: 'basic-yearly', label: 'Basic Yearly' },
    { value: 'premium-monthly', label: 'Premium Monthly' },
    { value: 'premium-yearly', label: 'Premium Yearly' },
    { value: 'enterprise-monthly', label: 'Enterprise Monthly' },
    { value: 'enterprise-yearly', label: 'Enterprise Yearly' },
  ];

  const handleSubmit = async () => {
    // Check if amount or plan changed but billing date didn't
    const amountChanged = formData.amount !== originalValues.amount;
    const planChanged = formData.planId !== originalValues.planId;
    const billingDateChanged = formData.nextBillingDate !== (originalValues.nextBillingDate ? new Date(originalValues.nextBillingDate).toISOString().split('T')[0] : '');

    if ((amountChanged || planChanged) && !billingDateChanged && !pendingChangesNextPeriod.amount && !pendingChangesNextPeriod.plan) {
      setShowBillingModal(true);
      return;
    }

    await saveChanges();
  };

  const handleBillingModalConfirm = (applyNextPeriod: boolean) => {
    setShowBillingModal(false);
    if (applyNextPeriod) {
      const amountChanged = formData.amount !== originalValues.amount;
      const planChanged = formData.planId !== originalValues.planId;
      setPendingChangesNextPeriod({
        amount: amountChanged,
        plan: planChanged,
      });
    }
    // If not applying next period, user needs to change billing date - just close modal
  };

  const saveChanges = async () => {
    // Validate if pending changes are required and billing date not changed
    const billingDateChanged = formData.nextBillingDate !== (originalValues.nextBillingDate ? new Date(originalValues.nextBillingDate).toISOString().split('T')[0] : '');
    const amountChanged = formData.amount !== originalValues.amount;
    const planChanged = formData.planId !== originalValues.planId;

    if ((amountChanged || planChanged) && !billingDateChanged && !pendingChangesNextPeriod.amount && !pendingChangesNextPeriod.plan) {
      setError('Please update the Next Billing Date or choose to apply changes in the next billing period.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await adminApi.updateSubscription(subscription.subscriptionId, {
        types: formData.types,
        status: formData.status,
        planId: formData.planId,
        billing: {
          amount: formData.amount,
          nextBillingDate: formData.nextBillingDate,
        },
        applyNextPeriod: pendingChangesNextPeriod.amount || pendingChangesNextPeriod.plan,
      });

      const updatedSubscription: AdminSubscriptionRecord = {
        ...subscription,
        types: formData.types,
        status: formData.status,
        planId: formData.planId,
        billing: {
          ...subscription.billing,
          amount: formData.amount,
          nextBillingDate: formData.nextBillingDate,
        },
      };

      onSuccess(updatedSubscription);
    } catch (err: any) {
      setError(err.message || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  // Effect to save after setting pending changes
  useEffect(() => {
    if (pendingChangesNextPeriod.amount || pendingChangesNextPeriod.plan) {
      saveChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChangesNextPeriod]);

  if (!isOpen || !subscription) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm h-screen">
        <div className="bg-white dark:bg-surface w-full max-w-lg rounded-xl shadow-2xl flex flex-col max-h-[90vh] border dark:border-border-color border-light-border-color animate-in fade-in zoom-in duration-200">
          
          {/* Header */}
          <div className="p-6 border-b dark:border-border-color border-light-border-color flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2.5 bg-primary/10 rounded-xl">
                  <Tag className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold dark:text-white text-gray-900">
                    Edit Subscription
                  </h2>
                  <p className="text-sm dark:text-text-secondary text-gray-500">
                    {subscription.tenantName}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Type Section */}
            <div>
              <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                Subscription Types
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.types.map((typeId) => {
                  const typeConfig = getTypeConfig(typeId);
                  return (
                    <span
                      key={typeId}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${typeConfig.color}`}
                    >
                      {typeConfig.label}
                      <button
                        onClick={() => removeType(typeId)}
                        className="ml-1 hover:opacity-70"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  );
                })}
                {formData.types.length === 0 && (
                  <span className="text-sm dark:text-gray-500 text-gray-400 italic">No types assigned</span>
                )}
              </div>
              
              {/* Add Type Button */}
              <div className="relative" ref={typeDropdownRef}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowTypeSelector(!showTypeSelector)}
                  className="flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  Add Type
                  <ChevronDown 
                    className={`w-4 h-4 transition-transform duration-200 ${showTypeSelector ? 'rotate-180' : ''}`} 
                  />
                </Button>
                
                {/* Type Selector Dropdown */}
                {showTypeSelector && (
                  <div className="absolute left-0 top-full mt-2 w-64 rounded-xl shadow-lg dark:bg-gray-800 bg-white border dark:border-gray-700 border-gray-200 z-10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-2 max-h-64 overflow-y-auto">
                      {allTypes
                        .filter(t => !formData.types.includes(t.id))
                        .map((type) => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => addType(type.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${type.color}`}>
                              {type.label}
                            </span>
                          </button>
                        ))}
                      
                      {/* New Type Option */}
                      {!showNewTypeInput ? (
                        <button
                          onClick={() => setShowNewTypeInput(true)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-primary"
                        >
                          <Plus className="w-4 h-4" />
                          <span className="text-sm font-medium">Create New Type</span>
                        </button>
                      ) : (
                        <div className="px-2 pb-2 pt-3 border-t dark:border-gray-700 border-gray-200 mt-2">
                          <div className="flex flex-col gap-2">
                            <input
                              type="text"
                              value={newTypeName}
                              onChange={(e) => setNewTypeName(e.target.value)}
                              placeholder="Type name..."
                              className="w-full px-3 py-2 text-sm rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                              autoFocus
                              onKeyDown={(e) => e.key === 'Enter' && handleAddNewType()}
                            />
                            <Button size="sm" onClick={handleAddNewType} className="w-full">
                              Add Type
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <Dropdown<FormData['status']>
              label="Status"
              options={statusOptions.map(status => {
                const config = getStatusConfig(status);
                return {
                  value: status,
                  label: config.label,
                  icon: config.icon,
                  color: config.bg,
                };
              })}
              value={formData.status}
              onChange={(value) => updateField('status', value)}
            />

            {/* Plan */}
            <Dropdown
              label="Plan"
              options={planOptions}
              value={formData.planId}
              onChange={(value) => updateField('planId', value)}
            />
            {pendingChangesNextPeriod.plan && (
              <p className="text-xs text-amber-600 dark:text-amber-400 -mt-3 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Will be changed in next billing period
              </p>
            )}

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
                Amount (per month)
              </label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => updateField('amount', parseFloat(e.target.value) || 0)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  step="0.01"
                  min="0"
                />
              </div>
              {pendingChangesNextPeriod.amount && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Will be changed in next billing period
                </p>
              )}
            </div>

            {/* Next Billing Date */}
            <div>
              <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
                Next Billing Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={formData.nextBillingDate}
                  onChange={(e) => updateField('nextBillingDate', e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                />
              </div>
            </div>

            {/* Subscription Info */}
            <div className="pt-4 border-t dark:border-gray-700 border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-primary rounded-full"></div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">Subscription Info</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border dark:border-gray-700 border-gray-200">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Subscription ID</p>
                  <p className="font-mono text-xs dark:text-gray-200 text-gray-800 break-all">{subscription.subscriptionId}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border dark:border-gray-700 border-gray-200">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Tenant ID</p>
                  <p className="font-mono text-xs dark:text-gray-200 text-gray-800 break-all">{subscription.tenantId}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/30">
            <div className="flex gap-3">
              <Button 
                variant="secondary" 
                onClick={onClose} 
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                className="flex-1"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Change Confirmation Modal */}
      <BillingChangeModal
        isOpen={showBillingModal}
        onConfirm={handleBillingModalConfirm}
        onCancel={() => setShowBillingModal(false)}
      />

      {/* Click outside to close type selector */}
      {showTypeSelector && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowTypeSelector(false);
            setShowNewTypeInput(false);
          }}
        />
      )}
    </>
  );
};

export default EditSubscriptionModal;
